import { useState, useEffect, useCallback, useRef } from 'react';
import { Boxes, Box, Package, GitBranch, RefreshCw, AlertTriangle, Check, Plus, Pencil, Trash2, X, Save, type LucideIcon } from 'lucide-react';
import { apiService, PackGroupDTO, PackGroupInput, PackSummaryDTO } from '@/services/api.service';
import { clearPackTextureCache } from '@/services/packAssets';
import { clearConfigListCache } from '@/components/config/configListCache';
import { isSyncWarning } from '@/components/config/SyncConflicts';
import { useGameStore } from '@/stores/game.store';
import { usePacksBranchStore } from '@/stores/packsBranch.store';
import { GAMES, getGameById, projectMatches } from '@/config/games';
import styles from './ConfigOverviewPage.module.scss';
import form from '@/components/config/PackForm.module.scss';

export function ConfigOverviewPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const game = getGameById(activeGameId);
  const [groups, setGroups] = useState<PackGroupDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PackGroupInput | null>(null);

  const reload = useCallback(async () => {
    const res = await apiService.getPacksGroups();
    if (res.data) setGroups(res.data.groups);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Après une bascule de branche (carte de groupe), recharge les groupes (branches à jour) ;
  // les cartes re-scannent via le prop scanToken (pas de remount, sinon la sync groupée se relancerait).
  const branchVersion = usePacksBranchStore((s) => s.version);
  const branchVersionRef = useRef(branchVersion);
  useEffect(() => {
    if (branchVersion !== branchVersionRef.current) {
      branchVersionRef.current = branchVersion;
      void reload();
    }
  }, [branchVersion, reload]);

  const visible = groups.filter((g) => projectMatches(g.projects, activeGameId));

  // Synchronisation groupée : un signal incrémenté déclenche la sync de chaque carte visible,
  // chaque carte gardant son propre feedback (spinner + messages). On compte les fins pour
  // libérer le bouton global quand tout est terminé.
  const [syncAllToken, setSyncAllToken] = useState(0);
  const [syncingAll, setSyncingAll] = useState(false);
  const pendingRef = useRef(0);

  const syncAll = () => {
    if (syncingAll || visible.length === 0) return;
    pendingRef.current = visible.length;
    setSyncingAll(true);
    setSyncAllToken((t) => t + 1);
  };

  const onCardSyncDone = useCallback(() => {
    pendingRef.current -= 1;
    if (pendingRef.current <= 0) setSyncingAll(false);
  }, []);

  const startCreate = () =>
    setEditing({
      id: '', name: '', type: 'block', color: game.accent.primary, projects: [activeGameId],
      behaviorRepo: '', resourceRepo: '', branch: 'develop', behaviorBranch: '', resourceBranch: '', resourceReadonly: false,
      cosmeticsSource: false, glyphSource: false, oversizedBlocks: false,
    });

  const startEdit = (g: PackGroupDTO) =>
    setEditing({
      id: g.id, name: g.name, type: g.type, color: g.color || '#8B3FF1', projects: g.projects ?? [],
      behaviorRepo: g.behaviorRepo, resourceRepo: g.resourceRepo, branch: g.branch || 'main',
      behaviorBranch: g.behaviorBranch ?? '', resourceBranch: g.resourceBranch ?? '', resourceReadonly: !!g.resourceReadonly,
      cosmeticsSource: !!g.cosmeticsSource, glyphSource: !!g.glyphSource, oversizedBlocks: !!g.oversizedBlocks,
    });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        {/* Titre et sous-titre vivent dans ConfigSettingsPage, qui porte la section « Pack » des Paramètres généraux.
            Le compte de groupes reste utile ici, à côté des actions. */}
        <div>
          <p className={styles.subtitle}>
            Projet {game.name}. {!loading && `${visible.length} groupe(s).`}
          </p>
        </div>
        {!editing && (
          <div className={styles.headerActions}>
            {visible.length > 0 && (
              <button className={styles.syncAllBtn} onClick={syncAll} disabled={syncingAll}
                title="Les packs déjà copiés se remettent à jour tout seuls (toutes les 3 minutes) quand GitHub bouge ; ce bouton le fait tout de suite.">
                <RefreshCw size={16} strokeWidth={2} className={syncingAll ? styles.spinning : ''} />
                {syncingAll ? 'Synchronisation…' : 'Tout synchroniser'}
              </button>
            )}
            <button className={styles.createBtn} style={{ backgroundColor: game.accent.primary }} onClick={startCreate}>
              <Plus size={16} /> Créer un groupe
            </button>
          </div>
        )}
      </header>

      {editing && (
        <GroupForm
          initial={editing}
          isNew={!groups.some((g) => g.id === editing.id)}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      )}

      {loading ? (
        <div className={styles.loadingState}><div className={styles.spinner} /><span>Chargement...</span></div>
      ) : visible.length === 0 && !editing ? (
        <div className={styles.warning}>
          <AlertTriangle size={16} />
          <span>Aucun groupe pour {game.name}. Clique sur « Créer un groupe ».</span>
        </div>
      ) : (
        <div className={styles.groupList}>
          {visible.map((g) => (
            <GroupCard
              key={g.id}
              group={g}
              onEdit={() => startEdit(g)}
              onDeleted={reload}
              syncAllToken={syncAllToken}
              onSyncDone={onCardSyncDone}
              scanToken={branchVersion}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function GroupForm({ initial, isNew, onCancel, onSaved }: { initial: PackGroupInput; isNew: boolean; onCancel: () => void; onSaved: () => void }) {
  const [g, setG] = useState<PackGroupInput>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof PackGroupInput>(k: K, v: PackGroupInput[K]) => setG((s) => ({ ...s, [k]: v }));

  const toggleProject = (id: string) =>
    set('projects', g.projects.includes(id) ? g.projects.filter((p) => p !== id) : [...g.projects, id]);

  const save = async () => {
    if (!g.id.trim()) { setError('Identifiant requis'); return; }
    if (g.projects.length === 0) { setError('Sélectionne au moins un projet'); return; }
    setSaving(true);
    setError(null);
    const res = await apiService.savePackGroup(g);
    setSaving(false);
    if (res.data?.success) onSaved();
    else setError(res.data?.message || res.error || 'Échec');
  };

  return (
    <div className={styles.formCard}>
      <div className={styles.formHead}>
        <h3>{isNew ? 'Nouveau groupe' : `Éditer ${g.id}`}</h3>
        <button className={styles.iconBtn} onClick={onCancel} title="Fermer"><X size={16} /></button>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Identifiant (technique)</label>
          <input className={form.input} value={g.id} disabled={!isNew} onChange={(e) => set('id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} placeholder="ex. ngmod" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Nom affiché</label>
          <input className={form.input} value={g.name} onChange={(e) => set('name', e.target.value)} placeholder="ex. NgMod" />
        </div>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Type (badge)</label>
          <input className={form.input} value={g.type} onChange={(e) => set('type', e.target.value)} placeholder="block, pack…" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Couleur</label>
          <div className={styles.colorRow}>
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(g.color) ? g.color : '#8B3FF1'} onChange={(e) => set('color', e.target.value)} />
            <input className={form.input} value={g.color} onChange={(e) => set('color', e.target.value)} placeholder="#8B3FF1" />
          </div>
        </div>
      </div>

      <div className={form.field}>
        <label className={form.label}>Projets</label>
        <div className={styles.projectChips}>
          {GAMES.map((p) => {
            const on = g.projects.includes(p.id);
            return (
              <button key={p.id} type="button" className={`${styles.projChip} ${on ? styles.projChipOn : ''}`}
                style={on ? { borderColor: p.accent.primary, backgroundColor: `${p.accent.primary}22`, color: p.accent.primary } : undefined}
                onClick={() => toggleProject(p.id)}>
                {on && <Check size={13} />} {p.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Repo Behavior (URL git)</label>
          <input className={form.input} value={g.behaviorRepo} onChange={(e) => set('behaviorRepo', e.target.value)} placeholder="https://github.com/…" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Repo Resource (URL git)</label>
          <input className={form.input} value={g.resourceRepo} onChange={(e) => set('resourceRepo', e.target.value)} placeholder="https://github.com/…" />
        </div>
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Branche par défaut</label>
          <input className={form.input} value={g.branch} onChange={(e) => set('branch', e.target.value)} placeholder="develop" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Branche Resource (si différente)</label>
          <input className={form.input} value={g.resourceBranch} onChange={(e) => set('resourceBranch', e.target.value)} placeholder="(défaut : branche par défaut)" />
        </div>
      </div>

      <label className={form.checkRow}>
        <input type="checkbox" checked={g.resourceReadonly} onChange={(e) => set('resourceReadonly', e.target.checked)} />
        <span>Resource pack en lecture seule (repo externe, pas de publication)</span>
      </label>

      <label className={form.checkRow}>
        <input type="checkbox" checked={g.cosmeticsSource} onChange={(e) => set('cosmeticsSource', e.target.checked)} />
        <span>Pack des cosmetics du projet (ornements / rank tags créés depuis Création)</span>
      </label>

      <label className={form.checkRow}>
        <input type="checkbox" checked={g.glyphSource} onChange={(e) => set('glyphSource', e.target.checked)} />
        <span>Pack de l'atlas de glyphes du projet (glyph_F1.png)</span>
      </label>

      <label className={form.checkRow}>
        <input type="checkbox" checked={g.oversizedBlocks} onChange={(e) => set('oversizedBlocks', e.target.checked)} />
        <span>Gros blocs de déco : détection des modèles trop gros pour un bloc et conversion en objets PropsiMod (NG Propsi)</span>
      </label>

      {error && <div className={form.error}><AlertTriangle size={14} /> {error}</div>}

      <div className={form.actions}>
        <button className={form.saveBtn} onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button className={styles.cancelBtn} onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

function GroupCard({
  group,
  onEdit,
  onDeleted,
  syncAllToken = 0,
  onSyncDone,
  scanToken = 0,
}: {
  group: PackGroupDTO;
  onEdit: () => void;
  onDeleted: () => void;
  syncAllToken?: number;
  onSyncDone?: () => void;
  scanToken?: number;
}) {
  const [syncing, setSyncing] = useState(false);
  const [messages, setMessages] = useState<string[] | null>(null);
  const [conflicts, setConflicts] = useState<string[]>([]);
  const [packs, setPacks] = useState<PackSummaryDTO[] | null>(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [switching, setSwitching] = useState<string | null>(null);
  const accent = group.color || '#8B3FF1';
  // Branche de travail du groupe. La resource d'un pack en lecture seule (vanilla ZtechNetwork,
  // sur master) n'est jamais basculée par le backend : on l'affiche donc telle quelle.
  const workBranch = group.behaviorBranch || group.branch || 'develop';

  // Re-scan au montage et après une bascule globale de branche (scanToken change) — sans remonter la carte.
  useEffect(() => {
    apiService.getPacksScan(group.id).then((r) => setPacks(r.data?.packs ?? []));
  }, [group.id, scanToken]);

  const runSync = useCallback(async () => {
    setSyncing(true);
    setMessages(null);
    const res = await apiService.syncPacksGroup(group.id);
    clearPackTextureCache(group.id);
    clearConfigListCache(group.id); // le contenu cloné a changé : on invalide le cache de listes
    // La sync garde le travail non publié par-dessus GitHub : les messages disent combien de
    // fichiers locaux sont conservés / fusionnés, et listent les conflits éventuels.
    setMessages(res.data?.messages ?? [res.error ?? 'Erreur de synchronisation']);
    setConflicts(res.data?.conflicts ?? []);
    const scan = await apiService.getPacksScan(group.id);
    setPacks(scan.data?.packs ?? []);
    setSyncing(false);
  }, [group.id]);

  const sync = () => { void runSync(); };

  // Sync groupée déclenchée depuis le header (incrément de syncAllToken).
  useEffect(() => {
    if (!syncAllToken) return; // pas au montage
    let cancelled = false;
    (async () => {
      try {
        await runSync();
      } finally {
        if (!cancelled) onSyncDone?.();
      }
    })();
    return () => { cancelled = true; };
    // Déclenché uniquement par le changement de token (pas par l'identité de runSync/onSyncDone).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncAllToken]);

  const del = async () => {
    await apiService.deletePackGroup(group.id);
    onDeleted();
  };

  /** Bascule CE groupe sur une branche puis resynchronise (le backend persiste + re-checkout). */
  const switchBranch = async (b: string) => {
    if (b === workBranch) return;
    setSwitching(b);
    setMessages(null);
    const res = await apiService.switchPackGroupBranch(group.id, b);
    clearPackTextureCache(group.id);
    clearConfigListCache(group.id);
    setMessages(res.data?.messages ?? [res.error ?? 'Bascule de branche en échec']);
    setConflicts(res.data?.conflicts ?? []);
    // Les catalogues (Blocks/Items…) affichent du contenu cloné : ils doivent refetcher.
    usePacksBranchStore.getState().bump();
    const scan = await apiService.getPacksScan(group.id);
    setPacks(scan.data?.packs ?? []);
    setSwitching(null);
    onSyncDone?.();
  };

  return (
    <div className={styles.groupCard} style={{ borderLeftColor: accent }}>
      <div className={styles.groupHead}>
        <span className={styles.groupBadge} style={{ backgroundColor: accent }}>{group.type || 'pack'}</span>
        <div className={styles.groupTitleBlock}>
          <h3 className={styles.groupName}>{group.name || group.id}</h3>
          <span className={styles.groupId}>{group.id} · {(group.projects ?? []).map((p) => getGameById(p).name).join(', ')}</span>
        </div>
        <div className={styles.groupActions}>
          <button className={styles.iconBtn} onClick={onEdit} title="Éditer"><Pencil size={15} /></button>
          {confirmDel ? (
            <>
              <button className={styles.dangerBtn} onClick={del}>Confirmer</button>
              <button className={styles.iconBtn} onClick={() => setConfirmDel(false)}><X size={15} /></button>
            </>
          ) : (
            <button className={styles.iconBtn} onClick={() => setConfirmDel(true)} title="Supprimer"><Trash2 size={15} /></button>
          )}
          <button className={styles.syncButton} onClick={sync} disabled={syncing} style={{ backgroundColor: accent }}>
            <RefreshCw size={16} strokeWidth={2} className={syncing ? styles.spinning : ''} />
            {syncing ? 'Sync...' : 'Synchroniser'}
          </button>
        </div>
      </div>

      <div className={styles.repos}>
        <RepoRow icon={Box} label="Behavior" url={group.behaviorRepo} branch={group.behaviorBranch || group.branch} />
        <RepoRow icon={Package} label="Resource" url={group.resourceRepo} branch={group.resourceBranch || group.branch} />
      </div>

      {/* Bascule de branche PAR GROUPE (remplace l'ancienne bascule globale de la sidebar). */}
      <div className={styles.branchRow}>
        <GitBranch size={14} className={styles.repoIcon} />
        <span className={styles.repoLabel}>Branche</span>
        <div className={styles.branchSeg}>
          {['develop', 'main'].map((b) => (
            <button
              key={b}
              type="button"
              className={`${styles.branchBtn} ${workBranch === b ? styles.branchBtnOn : ''}`}
              disabled={!!switching || workBranch === b}
              onClick={() => void switchBranch(b)}
              title={`Basculer ce groupe sur « ${b} » et resynchroniser`}
            >
              {switching === b ? <RefreshCw size={12} className={styles.spinning} /> : null}
              {b}
            </button>
          ))}
        </div>
        {group.resourceReadonly && (
          <span className={styles.branchNote}>
            resource en lecture seule : reste sur {group.resourceBranch || 'sa branche'}
          </span>
        )}
      </div>

      {messages && (
        <ul className={styles.syncMessages}>
          {messages.map((m, i) => {
            const warn = isSyncWarning(m);
            return (
              <li key={i} className={warn ? styles.syncWarn : undefined}>
                {warn ? <AlertTriangle size={12} /> : <Check size={12} />} {m}
              </li>
            );
          })}
          {conflicts.length > 0 && (
            <li className={styles.syncWarn}>
              <AlertTriangle size={12} /> {conflicts.length} conflit{conflicts.length > 1 ? 's' : ''} à trancher dans « Envoyer »
              avant de pouvoir publier {conflicts.length > 1 ? 'ces fichiers' : 'ce fichier'} (rien n'est perdu).
            </li>
          )}
        </ul>
      )}

      {packs !== null && (
        <div className={styles.packsSummary}>
          <Boxes size={14} />
          {packs.length === 0
            ? <span>Aucun pack (synchronise le groupe pour cloner les repos).</span>
            : <span>{packs.length} pack(s) : {packs.map((p) => p.name || p.path).join(', ')}</span>}
        </div>
      )}
    </div>
  );
}

function RepoRow({ icon: Icon, label, url, branch }: { icon: LucideIcon; label: string; url: string; branch: string }) {
  return (
    <div className={styles.repoRow}>
      <Icon size={16} className={styles.repoIcon} />
      <span className={styles.repoLabel}>{label}</span>
      {url ? (
        <a className={styles.repoUrl} href={url} target="_blank" rel="noreferrer">{url.replace(/^https?:\/\//, '')}</a>
      ) : (
        <span className={styles.repoEmpty}>non configuré</span>
      )}
      {url && branch && <span className={styles.repoBranch}><GitBranch size={12} /> {branch}</span>}
    </div>
  );
}

export function ConfigPlaceholder({ title, icon: Icon }: { title: string; icon: LucideIcon }) {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{title}</h1>
      </header>
      <div className={styles.comingSoon}>
        <Icon size={48} strokeWidth={1.5} />
        <h3>{title}</h3>
        <p>Édition en cours de portage — bientôt disponible.</p>
      </div>
    </div>
  );
}
